import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { PhoneNumber } from '@navio/nsdk';
import { URLKeys, invalidateQueryExactMatch, noddiAsync } from 'noddi-async';

import { useAuthContext } from 'noddi-web';
import { useState } from 'react';

import { BenefitsCustomerSMSLogin } from '@components/Login/BenefitsCustomerSMSLogin';
import { NoddiAsyncError } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiButton, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import {
  getDateOfBirthValidationSchema,
  getMembershipNumberValidationSchema,
  nafMembershipFormSchema
} from './nafMembershipValidators';

const getDateOfBirthDto = (dateOfBirth: string | null) => {
  if (!dateOfBirth) {
    return null;
  }
  if (dateOfBirth === '' || dateOfBirth.length < 8) {
    return null;
  }
  const dateOfBirthStripped = dateOfBirth.replace(/[./-]/g, '');

  const year = dateOfBirthStripped.slice(-4);
  const month = dateOfBirthStripped.slice(2, 4);
  const day = dateOfBirthStripped.slice(0, 2);
  return `${year}-${month}-${day}`;
};

const formatDateInput = (value: string): string => {
  const digitsOnly = value.replace(/\D/g, '');

  if (digitsOnly.length <= 2) {
    return digitsOnly;
  }

  if (digitsOnly.length <= 4) {
    return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2)}`;
  }

  return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2, 4)}.${digitsOnly.slice(4, 8)}`;
};

type FormValues = z.input<typeof nafMembershipFormSchema>;

type Props = {
  setCustomErrorMessage: (message: string | null | NoddiAsyncError) => void;
};

export const NafMembershipValidationForm = ({ setCustomErrorMessage }: Props) => {
  const { userData, currentUserGroupId, isLoggedIn } = useAuthContext();

  const [membershipNumber, setMembershipNumber] = useState<number | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState<string | null>(null);

  const [isMembershipNumberUsed, setIsMembershipNumberUsed] = useState(true);

  const { mutateAsync } = noddiAsync.usePost({
    type: URLKeys.postNafMembership,
    queryConfig: {
      onSuccess: async ({ data }) => {
        const { membershipAdded } = data;

        if (membershipAdded) {
          await invalidateQueryExactMatch({
            urlKey: URLKeys.getIsNAFMember,
            input: { userGroupId: currentUserGroupId! }
          });
        }
      },
      onError: (error) => {
        setCustomErrorMessage(error);
      }
    }
  });

  const activateMembership = async (
    data: { membershipNumber: number | null; dateOfBirth: string | null },
    userGroupId: null | number,
    phoneNumber?: string
  ) => {
    setCustomErrorMessage(null);

    if (!userGroupId) {
      return;
    }

    if (!phoneNumber) {
      setCustomErrorMessage(
        t`Your profile does not have a registered phone number. Contact customer service and we will fix it for you`
      );
      return;
    }

    const memberNumber = data.membershipNumber;
    const dateOfBirthFormatted = getDateOfBirthDto(data.dateOfBirth);

    if (memberNumber) {
      await mutateAsync({
        userGroupId,
        memberNumber: memberNumber.toString(),
        phoneNumber: null,
        dateOfBirth: null
      });
    } else if (dateOfBirthFormatted) {
      await mutateAsync({
        userGroupId,
        memberNumber: null,
        // TODO: nsdk types PhoneNumber as an object, but backend accepts a plain string here
        phoneNumber: phoneNumber as unknown as PhoneNumber,
        dateOfBirth: dateOfBirthFormatted
      });
    }
  };

  const activateMemberShipAfterLogin = async (userGroupIdFromAPI: number | null, phoneNumber?: string) => {
    if (userGroupIdFromAPI && (dateOfBirth || membershipNumber)) {
      await activateMembership({ membershipNumber, dateOfBirth }, userGroupIdFromAPI, phoneNumber);
    }
  };

  const defaultValues: FormValues = {
    membershipNumber: null,
    dateOfBirth: ''
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: ({ value }) => {
        if (isMembershipNumberUsed) {
          const result = getMembershipNumberValidationSchema().safeParse(value);
          if (!result.success) {
            return { fields: z.flattenError(result.error).fieldErrors };
          }
        } else {
          const result = getDateOfBirthValidationSchema().safeParse(value);
          if (!result.success) {
            return { fields: z.flattenError(result.error).fieldErrors };
          }
        }
        return undefined;
      }
    },
    onSubmit: async ({ value }) => {
      setMembershipNumber(value.membershipNumber);
      setDateOfBirth(value.dateOfBirth || null);

      if (currentUserGroupId) {
        await activateMembership(
          {
            membershipNumber: value.membershipNumber,
            dateOfBirth: value.dateOfBirth || null
          },
          currentUserGroupId,
          userData?.user.phoneNumber ?? undefined
        );
      }
    }
  });

  const showLogin = !isLoggedIn && (membershipNumber || dateOfBirth);

  if (showLogin) {
    return (
      <div className='flex flex-col gap-4'>
        <BenefitsCustomerSMSLogin defaultOpen activateMemberShipAfterLogin={activateMemberShipAfterLogin} />
        <NoddiButton
          variant='outline'
          onClick={() => {
            setMembershipNumber(null);
            setDateOfBirth(null);
          }}
        >
          Back
        </NoddiButton>
      </div>
    );
  }

  return (
    <form.AppForm>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <NoddiBasicCard>
          <div className={cn('flex flex-col gap-2', !isMembershipNumberUsed && 'hidden')}>
            <form.AppField name='membershipNumber'>
              {(field) => <field.Input type='number' label={t`NAF membership number`} placeholder='1234...' />}
            </form.AppField>
            <div>
              <p>
                <Trans>Don't remember your NAF membership number?</Trans>
              </p>
              <NoddiButton
                variant='link'
                className='pl-0'
                onClick={() => {
                  form.reset();
                  setIsMembershipNumberUsed(!isMembershipNumberUsed);
                }}
              >
                <Trans>Register using date of birth instead</Trans>
              </NoddiButton>
            </div>
          </div>
          <div className={cn('flex flex-col gap-2', isMembershipNumberUsed && 'hidden')}>
            <form.AppField name='dateOfBirth'>
              {(field) => (
                <field.Input
                  label={t`Date of birth`}
                  placeholder={t`dd.mm.yyyy`}
                  onChange={(e) => {
                    const formattedValue = formatDateInput(e.target.value);
                    field.handleChange(formattedValue);
                  }}
                />
              )}
            </form.AppField>
            <NoddiButton
              variant='link'
              className='pl-0'
              onClick={() => {
                form.reset();
                setIsMembershipNumberUsed(!isMembershipNumberUsed);
              }}
            >
              <Trans>Register with membership number</Trans>
            </NoddiButton>
          </div>
          <form.SubscribeButton className='mt-5 w-full' label={t`Confirm membership`} />
        </NoddiBasicCard>
      </form>
    </form.AppForm>
  );
};
